import React, { FC, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { FormatOptionLabelMeta } from "react-select"

import Select, { ISelectOption, ISelectProps } from "components/Select/Select"
import Color from "components/Color/Color"
import ColorModel from "models/Color"
import useColors from "hooks/useColors"
import { EMPTY_GUID } from "config/constants"
import { Guid } from "@/types"
import ColorPreview from "components/Color/ColorPreview"

type SelectColorOption = ISelectOption<Guid | null>

export interface ISelectColorProps extends Omit<ISelectProps, "value" | "onChange"> {
    value?: SelectColorOption
    colors?: ColorModel[] | null
    onChange(colorId: Guid | null): void
    noPreferenceOption?: boolean
    small?: boolean
}

const SelectColor: FC<React.PropsWithChildren<ISelectColorProps>> = ({
    value,
    colors: suppliedColors,
    onChange,
    noPreferenceOption,
    ...rest
}) => {
    const { t } = useTranslation()

    const shouldLoadColors = suppliedColors === undefined

    const {
        colors: loadedColors,
        fetchColors,
    } = useColors()

    useEffect(() => {
        if (shouldLoadColors) {
            fetchColors()
        }
    }, [fetchColors, shouldLoadColors])

    const colors = shouldLoadColors ? loadedColors : suppliedColors

    const options = useMemo<SelectColorOption[] | undefined>(() => {
        if (!colors) return
        return [
            ...noPreferenceOption
                ? [
                    {
                        label: "",
                        value: null,
                    },
                    {
                        label: t("colors.noSpecialPaintDefault"),
                        value: EMPTY_GUID,
                        borderTop: true,
                    }
                ]
                : [
                    {
                        label: "",
                        value: null,
                    }
                ],
            ...colors.map(color => ({
                label: color.name,
                value: color.id,
            }))
        ]
    }, [colors, noPreferenceOption, t])

    function getOptionTemplate(option: SelectColorOption, search: string, labelMeta: FormatOptionLabelMeta<any>) {
        const color = colors?.find(c => c.id === option.value)
        const colorUnknown = noPreferenceOption && option.value === null

        if (labelMeta.context === "value" && rest.size === "small") {
            return (
                <ColorPreview
                    code={colorUnknown ? null : color?.code}
                    unknown={colorUnknown}
                />
            )
        }

        return (
            <Color
                color={color}
                highlight={search}
                condensed
                unknown={colorUnknown}
                clampedTooltip={labelMeta.context === "menu"}
            />
        )
    }

    function handleChange(option?: SelectColorOption) {
        onChange(option?.value || null)
    }

    return (
        <Select<Guid | null>
            defaultValue={value}
            onChange={option => handleChange(option) }
            options={options}
            optionTemplate={getOptionTemplate}
            optionTemplateContext="all"
            formatOptionValue={val => val?.name}
            isDisabled={!options}
            isClearable={!!value?.value && rest.size !== "small"}
            placeholder={!options ? t("common.loading") : undefined}
            testIdPrefix="customer-color"
            {...rest}
        />
    )
}

export default SelectColor
